import { useCallback, useMemo } from "react";
import { useFormikContext } from "formik";
import ButtonV2 from "components/Button";
import InputAdornment from "@mui/material/InputAdornment";
import { useTranslation } from "react-i18next";
import Div from "components/primitives/Div";
import XIcon from "components/Icons/XIcon";
import { Divider } from "@mui/material";
import RefreshIcon from "components/Icons/RefreshIcon";
import styled from "@emotion/styled";
import Tag from "components/Tag";
import CheckIcon from "components/Icons/CheckIcon";
import TextField from "components/Inputs/TextField";
import { useInfoMessage } from "hooks";
import { EmailStatus, type EmailStatusType } from "helpers/AthleteHelpers/AthleteHelpers";
import useAthleteSendEmailMutation from "components/AthleteHub/hooks/useAthleteSendEmailMutation";
import useAthleteResendWelcomeEmailMutation from "components/AthleteHub/hooks/useAthleteResendWelcomeEmailMutation";
import useAthleteDeleteEmailMutation from "components/AthleteHub/hooks/useAthleteDeleteEmailMutation";
import useCountdown from "hooks/useCountdown";
export { getSchema } from "../validate";

const DangerButtonLink = styled(ButtonV2)(({ theme }) => ({
  "&.MuiButton-link": {
    color: theme.palette.error.main,

    "&:hover": {
      backgroundColor: theme.palette.error.light,
    },

    "&:disabled, &.Mui-disabled": {
      opacity: 0.3,
      color: theme.palette.primary.main,
      border: `2px solid ${theme.palette.primary.main}`,
    },

    "&:focus-visible": {
      outline: `2px solid ${theme.palette.primary.main}`,
    },

    "&:active": {
      backgroundColor: theme.palette.error.light,
    },
  },

  "& .MuiButton-startIcon": {
    color: theme.palette.error.main,
  },
}));

const ButtonLink = styled(ButtonV2)(() => ({
  "& .MuiButton-startIcon.MuiButton-iconSizeSmall": {
    marginRight: "6px",
    marginLeft: "-2px",
  },
}));

/** The subset of the athlete form values this field reads. */
interface AthleteEmailFormValues {
  email: string | null;
  pendingEmail: string | null;
}

function EmailStatusTag(props: { status: EmailStatusType | null }) {
  const { status } = props;
  const { t } = useTranslation();

  if (status === EmailStatus.Sent) {
    return (
      <Tag slotProps={{ root: { sx: { color: "grey.120" } } }} icon={<CheckIcon />}>
        {t("global.invite_status_sent")}
      </Tag>
    );
  }

  if (status === EmailStatus.Joined) {
    return <Tag color="success">{t("global.invite_status_joined")}</Tag>;
  }

  return null;
}

interface FormFieldEmailProps {
  email: string | null;
  pendingEmail: string | null;
  /** Absent while the athlete is still being created: the invite actions need an athlete to act on. */
  athleteId?: string;
}

function FormFieldEmail(props: FormFieldEmailProps) {
  const { t } = useTranslation();
  const { values, errors, setFieldValue } = useFormikContext<AthleteEmailFormValues>();

  const infoSuccess = useInfoMessage({ type: "success" });
  const infoError = useInfoMessage({ type: "error" });

  const { start, stop, secondsRemaining } = useCountdown();

  const { trigger: resendInviteTrigger } = useAthleteSendEmailMutation({ athleteId: props.athleteId });
  const { trigger: deleteEmailTrigger } = useAthleteDeleteEmailMutation({ athleteId: props.athleteId });
  const { trigger: resendWelcomeEmailTrigger } = useAthleteResendWelcomeEmailMutation({ athleteId: props.athleteId });

  /**
   * The status of the address the field holds, which is also what the invite actions act on: the athlete's pending
   * change is awaiting acceptance, their confirmed address has been joined, and any other address is not theirs yet.
   */
  const emailStatus = useMemo(() => {
    if (!values.email) return null;
    if (values.email === props.pendingEmail) return EmailStatus.Sent;
    if (values.email === props.email && !props.pendingEmail) return EmailStatus.Joined;
    return null;
  }, [props.email, props.pendingEmail, values.email]);

  // An empty field is valid (the address is optional), so only a populated invalid value is an error.
  const hasErrors = useMemo(() => Boolean(values.email) && Boolean(errors.email), [errors.email, values.email]);

  const helperText = useMemo(() => {
    if (hasErrors && errors.email) return t(errors.email);
    // An address the athlete does not have yet is the only one that gets invited when the form is saved.
    if (!!values.email && !emailStatus) return t("athlete.invite_email_helper_text");
    return;
  }, [emailStatus, errors.email, hasErrors, t, values.email]);

  const EndAdornment = useMemo(
    () => (
      <InputAdornment position="end">
        <EmailStatusTag status={emailStatus} />
      </InputAdornment>
    ),
    [emailStatus]
  );

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setFieldValue("email", event.target.value);
      if (secondsRemaining > 0) {
        stop();
      }
    },
    [secondsRemaining, setFieldValue, stop]
  );

  const handleResendWelcomeEmail = useCallback(async () => {
    if (!props.athleteId) return;

    resendWelcomeEmailTrigger(
      { athleteId: props.athleteId },
      {
        onSuccess: () => {
          infoSuccess({ message: t("athlete.welcome_email_resend_success") });
        },
        onError: (error) => {
          console.error("Error sending welcome email:", error);
          infoError({ message: t("errors.generic_message") });
        },
      }
    );
  }, [infoError, infoSuccess, props.athleteId, resendWelcomeEmailTrigger, t]);

  const handleResendInvite = useCallback(async () => {
    if (!props.athleteId) return;

    resendInviteTrigger(
      { athleteId: props.athleteId },
      {
        onSuccess: () => {
          start();
          infoSuccess({ message: t("athlete.invite_resend_success") });
        },
        onError: (error) => {
          console.error("Error resending invite:", error);
          infoError({ message: t("errors.generic_message") });
        },
      }
    );
  }, [resendInviteTrigger, props.athleteId, start, infoSuccess, t, infoError]);

  const handleCancelInvite = useCallback(async () => {
    if (!props.athleteId) return;

    const email = props.email ?? null;

    deleteEmailTrigger(
      { athleteId: props.athleteId, email: email },
      {
        onSuccess: () => {
          stop();
          infoSuccess({ message: t("athlete.invite_cancel_success") });
        },
        onError: (error) => {
          console.error("Error cancelling invite:", error);
          infoError({ message: t("errors.generic_message") });
        },
      }
    );
  }, [props.email, props.athleteId, deleteEmailTrigger, stop, infoSuccess, t, infoError]);

  return (
    <Div sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
      <TextField
        label={t("global.email_address_optional_label")}
        placeholder="name@email.com"
        autoComplete="email"
        fullWidth
        value={values.email || ""}
        onChange={handleChange}
        error={hasErrors}
        helperText={helperText}
        slotProps={{ input: { endAdornment: EndAdornment } }}
      />
      {emailStatus === EmailStatus.Sent ? (
        <Div sx={{ display: "flex", gap: "12px", alignItems: "center", width: "100%" }}>
          <ButtonLink
            variant="link"
            size="small"
            startIcon={<RefreshIcon />}
            onClick={handleResendInvite}
            disabled={secondsRemaining > 0}
          >
            {secondsRemaining === 0
              ? t("global.resend_invite_label")
              : t("global.resend_counter_label", { count: secondsRemaining })}
          </ButtonLink>
          <Divider orientation="vertical" flexItem variant="middle" sx={{ borderColor: "grey.40" }} />
          <DangerButtonLink variant="link" size="small" startIcon={<XIcon />} onClick={handleCancelInvite}>
            {t("global.cancel_invite_label")}
          </DangerButtonLink>
        </Div>
      ) : emailStatus === EmailStatus.Joined ? (
        <Div>
          <ButtonLink variant="link" size="small" startIcon={<RefreshIcon />} onClick={handleResendWelcomeEmail}>
            {t("global.resend_welcome_email_label")}
          </ButtonLink>
        </Div>
      ) : null}
    </Div>
  );
}

export default FormFieldEmail;
